"use client";

import { Menu as DropdownMenuPrimitive } from "@base-ui/react/menu";
import {
  CheckIcon,
  ChevronRightIcon,
  DotFilledIcon,
} from "@radix-ui/react-icons";
import * as React from "react";

import { renderAsChild } from "@/components/ui/render-as-child";
import { cn } from "@/utils/lib";
import { resolveClassName } from "@/utils/resolve-class-name";

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = ({
  asChild,
  children,
  nativeButton = false,
  render,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger> & {
  asChild?: boolean;
}) => (
  <DropdownMenuPrimitive.Trigger
    nativeButton={nativeButton}
    render={render ?? renderAsChild(asChild, children)}
    {...props}
  >
    {asChild ? null : children}
  </DropdownMenuPrimitive.Trigger>
);
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = (
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Portal>,
) => <DropdownMenuPrimitive.Portal {...props} />;

const DropdownMenuSub = DropdownMenuPrimitive.SubmenuRoot;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const dropdownMenuContentClassName = `
  bg-popover text-muted-foreground z-100 min-w-32 overflow-hidden rounded-md
  border p-1 shadow-md outline-hidden origin-(--transform-origin)
  transition duration-150 ease-out
  data-ending-style:scale-95 data-starting-style:scale-95
  data-ending-style:opacity-0 data-starting-style:opacity-0
`;

const DropdownMenuSubTrigger = ({
  ref,
  className,
  inset,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubmenuTrigger> & {
  inset?: boolean;
}) => {
  return (
    <DropdownMenuPrimitive.SubmenuTrigger
      ref={ref}
      className={(state) =>
        cn(
          `
            flex
            cursor-default items-center
            rounded-sm px-2
            py-1.5 text-sm text-muted-foreground outline-hidden select-none data-highlighted:bg-accent data-highlighted:text-foreground
            data-popup-open:bg-accent data-popup-open:text-foreground
          `,
          inset && "pl-8",
          resolveClassName(className, state),
        )
      }
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto size-4" />
    </DropdownMenuPrimitive.SubmenuTrigger>
  );
};
DropdownMenuSubTrigger.displayName = "DropdownMenuSubTrigger";

const DropdownMenuSubContent = ({
  className,
  align = "start",
  alignOffset = -3,
  side = "right",
  sideOffset = 0,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Popup> &
  Pick<
    React.ComponentProps<typeof DropdownMenuPrimitive.Positioner>,
    "align" | "alignOffset" | "side" | "sideOffset"
  >) => (
  <DropdownMenuPortal>
    <DropdownMenuPrimitive.Positioner
      align={align}
      alignOffset={alignOffset}
      className="isolate z-100"
      side={side}
      sideOffset={sideOffset}
    >
      <DropdownMenuPrimitive.Popup
        className={(state) =>
          cn(
            dropdownMenuContentClassName,
            "shadow-lg",
            resolveClassName(className, state),
          )
        }
        {...props}
      />
    </DropdownMenuPrimitive.Positioner>
  </DropdownMenuPortal>
);
DropdownMenuSubContent.displayName = "DropdownMenuSubContent";

const DropdownMenuContent = ({
  className,
  sideOffset = 4,
  align = "start",
  alignOffset = 0,
  side = "bottom",
  onCloseAutoFocus,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Popup> &
  Pick<
    React.ComponentProps<typeof DropdownMenuPrimitive.Positioner>,
    "align" | "alignOffset" | "side" | "sideOffset"
  > & {
    onCloseAutoFocus?: (event: { preventDefault: () => void }) => void;
  }) => {
  const finalFocus = React.useMemo(() => {
    if (!onCloseAutoFocus) {
      return undefined;
    }

    return () => {
      let defaultPrevented = false;
      onCloseAutoFocus({
        preventDefault: () => {
          defaultPrevented = true;
        },
      });
      return defaultPrevented ? false : true;
    };
  }, [onCloseAutoFocus]);

  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        className="isolate z-100"
        side={side}
        sideOffset={sideOffset}
      >
        <DropdownMenuPrimitive.Popup
          finalFocus={finalFocus}
          className={(state) =>
            cn(dropdownMenuContentClassName, resolveClassName(className, state))
          }
          {...props}
        />
      </DropdownMenuPrimitive.Positioner>
    </DropdownMenuPrimitive.Portal>
  );
};
DropdownMenuContent.displayName = "DropdownMenuContent";

export const dropdownMenuItemClassName =
  "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm text-foreground outline-hidden transition-colors data-highlighted:bg-popover-foreground data-highlighted:text-primary data-disabled:pointer-events-none data-disabled:opacity-50";

type DropdownMenuItemClickEvent = Parameters<
  NonNullable<
    React.ComponentProps<typeof DropdownMenuPrimitive.Item>["onClick"]
  >
>[0];

type DropdownMenuItemProps = Omit<
  React.ComponentProps<typeof DropdownMenuPrimitive.Item>,
  "closeOnClick" | "onClick" | "onSelect"
> & {
  asChild?: boolean;
  closeOnClick?: React.ComponentProps<
    typeof DropdownMenuPrimitive.Item
  >["closeOnClick"];
  closeOnSelect?: boolean;
  inset?: boolean;
  onClick?: React.ComponentProps<typeof DropdownMenuPrimitive.Item>["onClick"];
  onSelect?: (event: DropdownMenuItemClickEvent) => void;
};

const DropdownMenuItem = ({
  ref,
  asChild,
  className,
  closeOnClick,
  closeOnSelect,
  inset,
  children,
  onClick,
  onSelect,
  render,
  ...props
}: DropdownMenuItemProps) => {
  const handleClick = React.useCallback(
    (event: DropdownMenuItemClickEvent) => {
      onClick?.(event);
      onSelect?.(event);
    },
    [onClick, onSelect],
  );

  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      closeOnClick={closeOnSelect ?? closeOnClick}
      render={render ?? renderAsChild(asChild, children)}
      className={(state) =>
        cn(
          `
            relative
            flex cursor-default
            items-center rounded-sm px-2 py-1.5 text-sm text-foreground outline-hidden transition-colors
            select-none data-disabled:pointer-events-none data-disabled:opacity-50
            data-highlighted:bg-popover-foreground data-highlighted:text-primary
          `,
          inset && "pl-8",
          resolveClassName(className, state),
        )
      }
      onClick={handleClick}
      {...props}
    >
      {asChild ? null : children}
    </DropdownMenuPrimitive.Item>
  );
};
DropdownMenuItem.displayName = "DropdownMenuItem";

export const DropdownMenuItemWithoutAction = ({
  ref,
  className,
  inset,
  ...props
}: React.ComponentProps<"div"> & {
  inset?: boolean;
}) => (
  <div
    ref={ref}
    className={cn(
      `
        relative
        flex cursor-default
        items-center rounded-sm px-2 py-1.5 text-sm text-foreground outline-hidden transition-colors
        select-none hover:bg-background hover:text-primary
        data-disabled:pointer-events-none data-disabled:opacity-50
      `,
      inset && "pl-8",
      className,
    )}
    {...props}
  />
);
DropdownMenuItemWithoutAction.displayName = "DropdownMenuItemWithoutAction";

const DropdownMenuCheckboxItem = ({
  ref,
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) => {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      checked={checked}
      className={(state) =>
        cn(
          `
            relative flex
            cursor-default items-center rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden
            transition-colors select-none data-disabled:pointer-events-none data-disabled:opacity-50
            data-highlighted:bg-accent data-highlighted:text-accent-foreground
          `,
          resolveClassName(className, state),
        )
      }
      {...props}
    >
      <span className="absolute left-2 flex size-3.5 items-center justify-center">
        <DropdownMenuPrimitive.CheckboxItemIndicator>
          <CheckIcon className="size-4" />
        </DropdownMenuPrimitive.CheckboxItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
};
DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem";

const DropdownMenuRadioItem = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) => {
  return (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      className={(state) =>
        cn(
          `
            relative flex
            cursor-default items-center rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden
            transition-colors select-none data-disabled:pointer-events-none data-disabled:opacity-50
            data-highlighted:bg-accent data-highlighted:text-accent-foreground
          `,
          resolveClassName(className, state),
        )
      }
      {...props}
    >
      <span className="absolute left-2 flex size-3.5 items-center justify-center">
        <DropdownMenuPrimitive.RadioItemIndicator>
          <DotFilledIcon className="size-4 fill-current" />
        </DropdownMenuPrimitive.RadioItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
};
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";

const DropdownMenuLabel = ({
  ref,
  className,
  inset,
  ...props
}: React.ComponentProps<"div"> & {
  inset?: boolean;
}) => (
  <div
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className,
    )}
    {...props}
  />
);
DropdownMenuLabel.displayName = "DropdownMenuLabel";

const DropdownMenuSeparator = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={(state) =>
      cn("-mx-1 my-1 h-px bg-border-light", resolveClassName(className, state))
    }
    {...props}
  />
);
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    className={cn("ml-auto text-xs tracking-widest opacity-60", className)}
    {...props}
  />
);
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
